import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  Copy,
  FileText,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import type { QnaQuestion } from "@/lib/api/fddQa";
import type {
  DemoQnaQuestion,
  DemoQnaOutcome as SoWhatOutcome,
} from "./demoQnaQuestions";

interface ConversationMessage {
  role: "advisor" | "management";
  text: string;
  timestamp?: string;
}

// AI Check cell and thread copied from QuestionsList at f1927ac, adapted to demo builder state.
export function useDemoQnaAiCheck(
  questions: DemoQnaQuestion[],
  onOutcomesChange: (
    update: (
      previous: Record<string, SoWhatOutcome>,
    ) => Record<string, SoWhatOutcome>,
  ) => void,
  onFollowUpChange: (id: string, text: string) => void,
) {
  const allItems = questions
    .filter((q) => q.status !== "Rejected")
    .map((q) => ({
      ...q,
      category: q.metric,
      followUp: q.follow_up,
      dealImplication: q.deal_implication,
    }));
  const soWhatMap = Object.fromEntries(
    questions.map((q) => [q.id, q.soWhat ?? null]),
  );
  const setSoWhatMap = onOutcomesChange;
  const [selectedQaId, setSelectedQaId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [conversationMap, setConversationMap] = useState<
    Record<string, ConversationMessage[]>
  >({});
  const [analysisPlanShown, setAnalysisPlanShown] = useState<
    Record<string, boolean>
  >({});
  const [analysisRunning, setAnalysisRunning] = useState<
    Record<string, boolean>
  >({});
  const [rfiDraftShown, setRfiDraftShown] = useState<Record<string, boolean>>(
    {},
  );
  const [rfiDraftText, setRfiDraftText] = useState<Record<string, string>>({});
  const [rfiSent, setRfiSent] = useState<Record<string, boolean>>({});
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  function getOutcome(question: QnaQuestion) {
    return question.answer ? (soWhatMap[question.id] ?? null) : null;
  }
  function renderAiCheck(question: QnaQuestion) {
    const item = allItems.find((i) => i.id === question.id);
    if (!item || !item.answer)
      return <span className="text-muted-foreground text-[10px]">—</span>;
    // AI-determined outcome
    const sw = soWhatMap[item.id] ?? null;
    const labels: Record<string, { text: string; color: string }> = {
      "follow-up": {
        text: "Follow-up",
        color: "text-blue-600 bg-white border border-blue-200",
      },
      "deal-implication": {
        text: "Adjustment",
        color: "text-rose-600 bg-white border border-rose-200",
      },
      "request-info": {
        text: "RFI Request",
        color: "text-amber-600 bg-white border border-amber-200",
      },
      "request-analysis": {
        text: "Analysis",
        color: "text-purple-600 bg-white border border-purple-200",
      },
      clear: {
        text: "Resolved",
        color: "text-emerald-600 bg-white border border-emerald-200",
      },
    };
    if (sw && labels[sw]) {
      // Build inline detail based on AI Check type
      const getDetail = () => {
        if (sw === "follow-up") {
          return item.followUp ? (
            <div className="mt-1.5 text-[10px] text-foreground/70 leading-relaxed border-l-2 border-blue-300 pl-2">
              {item.followUp}
            </div>
          ) : null;
        }
        if (sw === "deal-implication") {
          return (
            <>
              <div className="mt-1.5 text-[10px] text-foreground/70 leading-relaxed border-l-2 border-rose-300 pl-2">
                {item.dealImplication ||
                  "Potential adjustment to normalised earnings identified from management response."}
              </div>
              {item.followUp && (
                <div className="mt-1.5 text-[10px] text-foreground/70 leading-relaxed border-l-2 border-blue-300 pl-2">
                  {item.followUp}
                </div>
              )}
            </>
          );
        }
        if (sw === "request-info") {
          const draft = rfiDraftText[item.id];
          return (
            <div className="mt-1.5 text-[10px] text-foreground/70 leading-relaxed border-l-2 border-amber-300 pl-2">
              {draft || "Additional documentation required from management."}
            </div>
          );
        }
        if (sw === "request-analysis") {
          const getAnalysisDesc = () => {
            const q = (item.question || "").toLowerCase();
            if (q.includes("revenue") || q.includes("cash basis"))
              return "Restate revenue from cash basis to accrual basis using invoice dates and delivery records.";
            if (q.includes("inventory") || q.includes("stock"))
              return "Revalue inventory using net realisable value and assess obsolescence provisions.";
            return "Additional analytical procedures required based on management response.";
          };
          return (
            <div className="mt-1.5 text-[10px] text-foreground/70 leading-relaxed border-l-2 border-purple-300 pl-2">
              {getAnalysisDesc()}
            </div>
          );
        }
        return null;
      };
      return (
        <div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedQaId(item.id);
              }}
              className={cn(
                "text-[10px] font-medium px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80",
                labels[sw].color,
              )}
            >
              {labels[sw].text}
            </button>
            {sw === "deal-implication" && item.followUp && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full text-blue-600 bg-blue-50">
                Follow-up
              </span>
            )}
            <button
              type="button"
              aria-label="Copy AI check"
              title="Copy AI check"
              className="ml-auto rounded p-1 text-muted-foreground hover:bg-muted"
              onClick={(event) => {
                event.stopPropagation();
                void navigator.clipboard
                  .writeText(
                    [item.dealImplication, item.followUp]
                      .filter(Boolean)
                      .join("\n\n"),
                  )
                  .then(() =>
                    toast({
                      title: "Copied",
                      description: "AI check copied to clipboard.",
                    }),
                  )
                  .catch(() =>
                    toast({
                      title: "Could not copy AI check",
                      variant: "destructive",
                    }),
                  );
              }}
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
          {getDetail()}
          {sw !== "clear" && (
            <div className="mt-1.5">
              <select
                aria-label="Change AI check status"
                value={sw}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const val = e.target.value as SoWhatOutcome;
                  setSoWhatMap((p) => ({ ...p, [item.id]: val }));
                  toast({
                    title: `Changed to ${{ "deal-implication": "Adjustment", "request-info": "RFI Request", "request-analysis": "Analysis", clear: "Resolved", "follow-up": "Follow-up" }[val] || val}`,
                  });
                }}
                className="text-[9px] text-muted-foreground/60 bg-transparent border-none outline-none cursor-pointer hover:text-foreground transition-colors"
              >
                <option value={sw}>Change status...</option>
                {sw !== "follow-up" && (
                  <option value="follow-up">Follow-up</option>
                )}
                {sw !== "deal-implication" && (
                  <option value="deal-implication">Adjustment</option>
                )}
                {sw !== "request-info" && (
                  <option value="request-info">RFI Request</option>
                )}
                {sw !== "request-analysis" && (
                  <option value="request-analysis">Analysis</option>
                )}
                <option value="clear">Resolved</option>
              </select>
            </div>
          )}
        </div>
      );
    }

    // Fallback — answered but no AI check yet
    return (
      <div className="flex items-center gap-1">
        <Loader2 className="w-3 h-3 text-muted-foreground/40 animate-spin shrink-0" />
        <span className="text-[10px] text-muted-foreground">Pending</span>
      </div>
    );
  }
  const panel = (
    <>
      {/* Side panel — conversation thread + So What? */}
      {selectedQaId &&
        (() => {
          const selectedItem = allItems.find((i) => i.id === selectedQaId);
          if (!selectedItem) return null;
          const area = selectedItem.category;
          const conversation = conversationMap[selectedQaId] || [];
          const currentSoWhat = soWhatMap[selectedQaId] ?? null;
          // Build initial thread from existing data
          const thread: (ConversationMessage & {
            isAiGenerated?: boolean;
            isFollowUp?: boolean;
          })[] = [
            {
              role: "advisor" as const,
              text: selectedItem.question,
              timestamp: "Initial question",
            },
            ...(selectedItem.answer
              ? [
                  {
                    role: "management" as const,
                    text: selectedItem.answer,
                    timestamp: "Response",
                  },
                ]
              : []),
            ...(selectedItem.followUp
              ? [
                  {
                    role: "advisor" as const,
                    text: selectedItem.followUp,
                    timestamp: "Auto-generated follow-up",
                    isAiGenerated: true,
                    isFollowUp: true,
                  },
                ]
              : []),
            ...conversation,
          ];

          return (
            <div
              key={selectedQaId}
              role="dialog"
              aria-label="Q&A Thread"
              className="fixed inset-y-0 right-0 w-[420px] bg-background border-l border-border shadow-xl z-50 flex flex-col animate-in slide-in-from-right-5 duration-200"
            >
              {/* Panel header */}
              <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between shrink-0">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {area || selectedItem.category}
                  </p>
                  <h3 className="text-sm font-semibold mt-0.5">Q&A Thread</h3>
                </div>
                <button
                  type="button"
                  aria-label="Close Q&A thread"
                  onClick={() => setSelectedQaId(null)}
                  className="p-1.5 rounded hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Conversation thread */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                {thread.map((msg, i) => (
                  <div
                    key={i}
                    className={cn(
                      "rounded-lg px-3.5 py-2.5 text-xs leading-relaxed",
                      msg.role === "advisor"
                        ? "bg-primary/5 border border-primary/10 ml-4"
                        : "bg-muted/50 border border-border/40 mr-4",
                      msg.isAiGenerated && "border-purple-200 bg-purple-50/50",
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span
                        className={cn(
                          "text-[10px] font-semibold uppercase tracking-wider",
                          msg.role === "advisor"
                            ? "text-primary/70"
                            : "text-foreground/50",
                        )}
                      >
                        {msg.role === "advisor" ? "Advisor" : "Management"}
                      </span>
                      {msg.isAiGenerated && (
                        <span className="text-[9px] font-medium text-purple-600 bg-purple-100 px-1.5 py-px rounded-full flex items-center gap-1">
                          <Sparkles className="w-2.5 h-2.5" /> AI-generated
                        </span>
                      )}
                      {msg.timestamp && (
                        <span className="text-[9px] text-muted-foreground/50">
                          · {msg.timestamp}
                        </span>
                      )}
                    </div>
                    {msg.isFollowUp ? (
                      <textarea
                        aria-label="Follow-up text"
                        defaultValue={msg.text}
                        className="text-foreground/80 whitespace-pre-line w-full bg-transparent border-none outline-none resize-none text-xs leading-relaxed min-h-[40px]"
                        rows={3}
                        onBlur={(e) => {
                          // Update followUp text on blur
                          const newText = e.target.value;
                          if (newText !== msg.text) {
                            onFollowUpChange(selectedQaId, newText);
                          }
                        }}
                      />
                    ) : (
                      <p className="text-foreground/80 whitespace-pre-line">
                        {msg.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* New message input */}
              <div className="px-5 py-3 border-t border-border/40 shrink-0">
                <div className="flex gap-2">
                  <input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newMessage.trim()) {
                        setConversationMap((prev) => ({
                          ...prev,
                          [selectedQaId]: [
                            ...(prev[selectedQaId] || []),
                            {
                              role: "advisor",
                              text: newMessage.trim(),
                              timestamp: "Just now",
                            },
                          ],
                        }));
                        setNewMessage("");
                      }
                    }}
                    placeholder="Type a follow-up..."
                    className="flex-1 text-xs px-3 py-2 rounded-md border border-border/60 bg-muted/20 outline-none focus:border-primary/40 transition-colors"
                  />
                  <button
                    onClick={() => {
                      if (!newMessage.trim()) return;
                      setConversationMap((prev) => ({
                        ...prev,
                        [selectedQaId]: [
                          ...(prev[selectedQaId] || []),
                          {
                            role: "advisor",
                            text: newMessage.trim(),
                            timestamp: "Just now",
                          },
                        ],
                      }));
                      setNewMessage("");
                    }}
                    className="px-3 py-2 rounded-md bg-foreground text-background text-xs font-medium hover:bg-foreground/90 transition-colors"
                  >
                    Send
                  </button>
                </div>
              </div>

              {/* AI-determined AI Check */}
              {currentSoWhat &&
                (() => {
                  // Build context-aware descriptions based on question/answer content
                  const answerText = (selectedItem.answer || "").toLowerCase();
                  const questionText = (
                    selectedItem.question || ""
                  ).toLowerCase();

                  const getAnalysisDesc = () => {
                    if (
                      answerText.includes("cash basis") ||
                      answerText.includes("cash accounting")
                    )
                      return "Management confirms revenue is recognised on a cash basis. The reported databook needs to be restated to an accrual basis to ensure comparability and compliance with NZ GAAP. This will generate a new version of the Reported Databook.";
                    if (
                      answerText.includes("fifo") ||
                      answerText.includes("lifo")
                    )
                      return "The inventory costing method disclosed differs from the method assumed in the databook. A restatement is required to align inventory valuation consistently across all periods.";
                    if (
                      questionText.includes("depreciation") ||
                      answerText.includes("diminishing value")
                    )
                      return "The depreciation method or useful life estimates differ from market standard. An analysis is required to restate depreciation on a consistent basis and assess the P&L impact.";
                    return "The response indicates the financial data was prepared on a basis that differs from what was assumed. An analytical restatement is required to produce a comparable, adjusted view. This will generate a new version of the Reported Databook.";
                  };

                  const getAdjustmentDesc = () => {
                    if (
                      answerText.includes("one-off") ||
                      answerText.includes("one-time") ||
                      answerText.includes("not expected to recur")
                    )
                      return "Management confirms this is a non-recurring item. A QoE adjustment has been suggested to remove it from normalised EBITDA.";
                    if (
                      answerText.includes("write-down") ||
                      answerText.includes("write-off") ||
                      answerText.includes("obsolete")
                    )
                      return "Management confirms a write-down was recorded. An NWC adjustment has been suggested to normalise working capital for the non-recurring inventory charge.";
                    return "This response reveals a material finding that impacts the deal analysis. An adjustment has been suggested in the Adjustment Summary.";
                  };

                  const getRfiDesc = () => {
                    if (
                      answerText.includes("need to check") ||
                      answerText.includes("would need to") ||
                      answerText.includes("get back to you")
                    )
                      return "Management could not provide full details. A new RFI item has been created requesting the missing documentation (e.g., facility agreement, covenant schedule).";
                    if (
                      questionText.includes("a/r") ||
                      questionText.includes("receivable") ||
                      questionText.includes("invoice")
                    )
                      return "The response references specific customer invoices. The aged receivables schedule and customer statements have been added to the RFI to verify the amounts.";
                    return "Additional documents or data are needed to complete the analysis. A new item has been added to the RFI tracker.";
                  };

                  const labels: Record<
                    string,
                    { text: string; desc: string; color: string }
                  > = {
                    "follow-up": {
                      text: "Follow-up",
                      desc: "Response is incomplete or raises new questions. A follow-up has been generated.",
                      color: "border-blue-200 bg-blue-50 text-blue-700",
                    },
                    "deal-implication": {
                      text: "Adjustment",
                      desc: getAdjustmentDesc(),
                      color: "border-rose-200 bg-white text-rose-600",
                    },
                    "request-info": {
                      text: "RFI Request",
                      desc: getRfiDesc(),
                      color: "border-amber-200 bg-white text-amber-600",
                    },
                    "request-analysis": {
                      text: "Analysis",
                      desc: getAnalysisDesc(),
                      color: "border-purple-200 bg-white text-purple-600",
                    },
                    clear: {
                      text: "Resolved",
                      desc: "Question is fully answered. No further action needed.",
                      color: "border-emerald-200 bg-white text-emerald-600",
                    },
                  };
                  const info = labels[currentSoWhat];
                  if (!info) return null;
                  return (
                    <div className="px-5 py-3.5 border-t border-border/40 shrink-0 bg-muted/10">
                      <div className="flex items-start gap-2.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary/50 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                              AI Check
                            </span>
                            <span
                              className={cn(
                                "text-[10px] font-medium px-1.5 py-px rounded-full border",
                                info.color,
                              )}
                            >
                              {info.text}
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            {info.desc}
                          </p>
                          {currentSoWhat === "request-analysis" &&
                            !analysisPlanShown[selectedQaId] &&
                            !analysisRunning[selectedQaId] && (
                              <button
                                onClick={() =>
                                  setAnalysisPlanShown((prev) => ({
                                    ...prev,
                                    [selectedQaId]: true,
                                  }))
                                }
                                className="mt-2 text-[11px] font-medium px-3 py-1.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center gap-1.5"
                              >
                                <Sparkles className="w-3 h-3" />
                                View Analysis Plan
                              </button>
                            )}
                          {currentSoWhat === "request-analysis" &&
                            analysisPlanShown[selectedQaId] &&
                            !analysisRunning[selectedQaId] && (
                              <div className="mt-2 rounded-md border border-purple-200 bg-purple-50/50 p-3">
                                <p className="text-[10px] font-semibold text-purple-700 uppercase tracking-wider mb-2">
                                  Proposed Analysis Plan
                                </p>
                                <ol className="text-[11px] text-foreground/80 leading-relaxed space-y-1.5 list-decimal pl-4">
                                  {answerText.includes("cash basis") ? (
                                    <>
                                      <li>
                                        Extract monthly revenue and receivables
                                        from the Reported Databook
                                      </li>
                                      <li>
                                        Calculate accrued revenue using invoice
                                        dates and delivery records
                                      </li>
                                      <li>
                                        Compute the cash-to-accrual adjustment
                                        per month across FY23–LTM25
                                      </li>
                                      <li>
                                        Restate the P&L on an accrual basis and
                                        regenerate the Reported Databook
                                      </li>
                                      <li>
                                        Update QoE bridge to reflect restated
                                        EBITDA
                                      </li>
                                    </>
                                  ) : answerText.includes(
                                      "diminishing value",
                                    ) ? (
                                    <>
                                      <li>
                                        Extract fixed asset register and current
                                        depreciation charges
                                      </li>
                                      <li>
                                        Recompute depreciation on a
                                        straight-line basis using
                                        market-standard useful lives
                                      </li>
                                      <li>
                                        Calculate the depreciation adjustment
                                        per period
                                      </li>
                                      <li>
                                        Update the P&L and balance sheet in the
                                        Reported Databook
                                      </li>
                                    </>
                                  ) : (
                                    <>
                                      <li>
                                        Review the accounting basis disclosed in
                                        management's response
                                      </li>
                                      <li>
                                        Identify the adjustments needed to align
                                        with standard reporting
                                      </li>
                                      <li>
                                        Compute the restated figures across all
                                        reporting periods
                                      </li>
                                      <li>
                                        Regenerate the Reported Databook with
                                        restated data
                                      </li>
                                    </>
                                  )}
                                </ol>
                                <div className="flex gap-2 mt-3">
                                  <button
                                    onClick={() => {
                                      setAnalysisPlanShown((prev) => ({
                                        ...prev,
                                        [selectedQaId]: false,
                                      }));
                                      setAnalysisRunning((prev) => ({
                                        ...prev,
                                        [selectedQaId]: true,
                                      }));
                                      timers.current.push(
                                        setTimeout(() => {
                                          setAnalysisRunning((prev) => ({
                                            ...prev,
                                            [selectedQaId]: false,
                                          }));
                                          setSoWhatMap((prev) => ({
                                            ...prev,
                                            [selectedQaId]: "clear",
                                          }));
                                        }, 3000),
                                      );
                                    }}
                                    className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                                  >
                                    Approve & Run
                                  </button>
                                  <button
                                    onClick={() =>
                                      setAnalysisPlanShown((prev) => ({
                                        ...prev,
                                        [selectedQaId]: false,
                                      }))
                                    }
                                    className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border/60 text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          {currentSoWhat === "request-analysis" &&
                            analysisRunning[selectedQaId] && (
                              <div className="mt-2 flex items-center gap-2 text-[11px] text-purple-600">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Running analysis — regenerating Reported
                                Databook…
                              </div>
                            )}
                          {currentSoWhat === "request-info" &&
                            !rfiSent[selectedQaId] &&
                            (() => {
                              // Generate draft RFI text from the question context
                              const getDraftRfi = () => {
                                if (rfiDraftText[selectedQaId])
                                  return rfiDraftText[selectedQaId];
                                const q = selectedItem.question.toLowerCase();
                                const a = (
                                  selectedItem.answer || ""
                                ).toLowerCase();
                                if (
                                  a.includes("need to check") ||
                                  a.includes("would need to")
                                )
                                  return `Please provide the full ${selectedItem.metric} documentation, including all terms, schedules and supporting agreements referenced in management's response.`;
                                if (
                                  q.includes("a/r") ||
                                  q.includes("receivable") ||
                                  q.includes("invoice")
                                )
                                  return `Please provide the aged receivables schedule as at the latest available date, together with customer statements for the top 5 debtors by outstanding balance.`;
                                return `Please provide the supporting documentation for ${selectedItem.metric} to complete the analysis. This was flagged during the Q&A process as requiring additional information.`;
                              };
                              if (!rfiDraftShown[selectedQaId]) {
                                return (
                                  <button
                                    onClick={() => {
                                      setRfiDraftShown((prev) => ({
                                        ...prev,
                                        [selectedQaId]: true,
                                      }));
                                      if (!rfiDraftText[selectedQaId])
                                        setRfiDraftText((prev) => ({
                                          ...prev,
                                          [selectedQaId]: getDraftRfi(),
                                        }));
                                    }}
                                    className="mt-2 text-[11px] font-medium px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center gap-1.5"
                                  >
                                    <FileText className="w-3 h-3" />
                                    Review RFI Draft
                                  </button>
                                );
                              }
                              return (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/50 p-3">
                                  <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1.5">
                                    Draft RFI Item
                                  </p>
                                  <textarea
                                    aria-label="Draft RFI item"
                                    value={
                                      rfiDraftText[selectedQaId] ??
                                      getDraftRfi()
                                    }
                                    onChange={(e) =>
                                      setRfiDraftText((prev) => ({
                                        ...prev,
                                        [selectedQaId]: e.target.value,
                                      }))
                                    }
                                    className="w-full text-[11px] text-foreground/80 bg-white border border-amber-200 rounded px-2.5 py-2 leading-relaxed resize-none outline-none focus:border-amber-400 transition-colors"
                                    rows={3}
                                  />
                                  <div className="flex gap-2 mt-2">
                                    <button
                                      onClick={() => {
                                        setRfiSent((prev) => ({
                                          ...prev,
                                          [selectedQaId]: true,
                                        }));
                                        setRfiDraftShown((prev) => ({
                                          ...prev,
                                          [selectedQaId]: false,
                                        }));
                                      }}
                                      className="text-[11px] font-medium px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center gap-1.5"
                                    >
                                      <ArrowRight className="w-3 h-3" />
                                      Send to RFI
                                    </button>
                                    <button
                                      onClick={() => {
                                        setRfiDraftShown((prev) => ({
                                          ...prev,
                                          [selectedQaId]: false,
                                        }));
                                        setSoWhatMap((prev) => ({
                                          ...prev,
                                          [selectedQaId]: "clear",
                                        }));
                                      }}
                                      className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-border/60 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                      Skip
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                          {currentSoWhat === "request-info" &&
                            rfiSent[selectedQaId] && (
                              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-600">
                                <Check className="w-3.5 h-3.5" />
                                Added to RFI Tracker
                              </div>
                            )}
                          {currentSoWhat === "deal-implication" && (
                            <button
                              onClick={() => {}}
                              className="mt-2 text-[11px] font-medium px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700 transition-colors flex items-center gap-1.5"
                            >
                              <FileText className="w-3 h-3" />
                              View Adjustment
                            </button>
                          )}
                          <button
                            onClick={() =>
                              setSoWhatMap((prev) => ({
                                ...prev,
                                [selectedQaId]: null,
                              }))
                            }
                            className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground mt-1 transition-colors"
                          >
                            Override →
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              {/* Override selector — only shown when user clicks Override or no AI recommendation */}
              {!currentSoWhat && selectedItem.answer && (
                <div className="px-5 py-3.5 border-t border-border/40 shrink-0 bg-muted/10">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Set AI Check
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      {
                        value: "follow-up" as SoWhatOutcome,
                        label: "Follow-up",
                      },
                      {
                        value: "deal-implication" as SoWhatOutcome,
                        label: "Adjustment",
                      },
                      {
                        value: "request-info" as SoWhatOutcome,
                        label: "RFI Request",
                      },
                      {
                        value: "request-analysis" as SoWhatOutcome,
                        label: "Analysis",
                      },
                      { value: "clear" as SoWhatOutcome, label: "Resolved" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() =>
                          setSoWhatMap((prev) => ({
                            ...prev,
                            [selectedQaId]: opt.value,
                          }))
                        }
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full border border-border/50 bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
    </>
  );
  return {
    getOutcome,
    renderAiCheck,
    panel,
    openQuestion: (question: QnaQuestion) => setSelectedQaId(question.id),
  };
}
